import { useState } from "react";
import { Share2, Copy, Users, Gift, Facebook, MessageCircle, Mail, Twitter } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";

interface ReferralLinkPopupProps {
  isOpen: boolean;
  onClose: () => void;
  competitionId: number;
  competitionTitle: string;
  referralUrl?: string;
}

export default function ReferralLinkPopup({
  isOpen,
  onClose,
  competitionId,
  competitionTitle,
  referralUrl
}: ReferralLinkPopupProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    if (!referralUrl) return;

    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      toast({
        title: "Link Copied! 📋",
        description: "Your referral link has been copied to clipboard",
      });
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      toast({
        title: "Copy Failed",
        description: "Please copy the link manually",
        variant: "destructive",
      });
    }
  };

  const handleShare = async (platform: string) => {
    const shareText = `🎉 Check out this amazing competition and we both get bonus entries! 🎁`;
    let shareLink = "";

    switch (platform) {
      case "whatsapp":
        shareLink = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + "\n\n" + referralUrl)}`;
        break;
      case "facebook":
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralUrl)}&quote=${encodeURIComponent(shareText)}`;
        break;
      case "twitter":
        shareLink = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(referralUrl)}`;
        break;
      case "email":
        shareLink = `mailto:?subject=${encodeURIComponent(`Check out this competition on Prize Drop!`)}&body=${encodeURIComponent(shareText + "\n\n" + referralUrl)}`;
        break;
    }

    if (shareLink) {
      // Open the sharing window
      const shareWindow = window.open(shareLink, '_blank', 'width=600,height=400,noopener,noreferrer');

      if (shareWindow) {
        // Wait for user confirmation
        const confirmed = await new Promise<boolean>((resolve) => {
          const confirmDialog = confirm(
            `Did you complete sharing on ${platform}? Click OK to confirm and earn your bonus entry, or Cancel if you didn't share.`
          );
          resolve(confirmDialog);
        });

        if (confirmed) {
          // Track the referral share
          try {
            await fetch(`/api/competitions/${competitionId}/share`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include',
              body: JSON.stringify({ 
                platform,
                shareType: 'referral'
              }),
            });
          } catch (error) {
            console.error('Error tracking referral share:', error);
          }
        }
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto mx-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-primary" />
            Share & Earn Bonus Entries!
          </DialogTitle>
          <DialogDescription>
            You just entered your first competition! Share your referral link with friends - you both get 35 entries each when they join.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Benefits Card */}
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="pt-4">
              <div className="text-center space-y-2">
                <Users className="h-8 w-8 text-primary mx-auto" />
                <h3 className="font-semibold text-primary">Win Together!</h3>
                <p className="text-sm text-gray-600">
                  When someone joins using your link, you get <strong>35 entries</strong> and your friend gets <strong>35 entries</strong>!
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Referral Link */}
          {referralUrl && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Your Referral Link:</label>
              <div className="flex gap-2">
                <Input
                  value={referralUrl}
                  readOnly
                  className="flex-1 text-sm"
                />
                <Button
                  onClick={handleCopyLink}
                  variant="outline"
                  size="sm"
                  className="px-3"
                >
                  <Copy className="h-4 w-4" />
                  {copied ? "Copied!" : "Copy"}
                </Button>
              </div>
            </div>
          )}

          {/* Share Buttons */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-center">Share on social media:</p>
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => handleShare("whatsapp")}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 text-green-700 border-green-200 hover:bg-green-100 hover:text-green-800"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp
              </Button>
              <Button
                onClick={() => handleShare("facebook")}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 text-blue-700 border-blue-200 hover:bg-blue-100 hover:text-blue-800"
              >
                <Facebook className="h-4 w-4" />
                Facebook
              </Button>
              <Button
                onClick={() => handleShare("twitter")}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 text-gray-800 border-gray-200 hover:bg-gray-100 hover:text-gray-900"
              >
                <Twitter className="h-4 w-4" />
                X (Twitter)
              </Button>
              <Button
                onClick={() => handleShare("email")}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 text-gray-700 border-gray-200 hover:bg-gray-100 hover:text-gray-800"
              >
                <Mail className="h-4 w-4" />
                Email
              </Button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button onClick={onClose} variant="outline" className="flex-1">
              Maybe Later
            </Button>
            <Button onClick={onClose} className="flex-1">
              Got It!
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}